/* ============================================
   AKI — THEME JS
   Vanilla custom elements. No framework, no jQuery.
   Every component is self-contained and safe to
   re-initialise inside the Shopify theme editor.
   ============================================ */
(function () {
  'use strict';

  /* ------------------------------------------
     Money formatting
     Shopify's format string is injected from Liquid
     so we never guess the currency or separators.
     ------------------------------------------ */
  var MONEY_FORMAT = (window.AkiTheme && window.AkiTheme.moneyFormat) || '{{amount}}';

  function formatMoney(cents) {
    var value = Number(cents) || 0;

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var decPart = parts[1] ? decimal + parts[1] : '';
      return intPart + decPart;
    }

    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', ',');
        case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  function t(key, fallback) {
    var strings = (window.AkiTheme && window.AkiTheme.strings) || {};
    return strings[key] || fallback;
  }

  /* ------------------------------------------
     Tiny event bus
     ------------------------------------------ */
  var bus = document.createElement('div');
  function emit(name, detail) { bus.dispatchEvent(new CustomEvent(name, { detail: detail })); }
  function on(name, fn) { bus.addEventListener(name, fn); }

  /* ------------------------------------------
     Cart API
     ------------------------------------------ */
  var Cart = {
    state: null,

    async get() {
      var res = await fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) throw new Error('cart fetch failed');
      this.state = await res.json();
      emit('cart:updated', this.state);
      return this.state;
    },

    async add(items) {
      var res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      });
      var data = await res.json();
      if (!res.ok) {
        var err = new Error(data.description || data.message || t('cartError', 'Das hat nicht geklappt.'));
        err.payload = data;
        throw err;
      }
      await this.get();
      return data;
    },

    async change(key, quantity) {
      var res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!res.ok) throw new Error('cart change failed');
      this.state = await res.json();
      emit('cart:updated', this.state);
      return this.state;
    }
  };

  window.AkiCart = Cart;

  /* Expose the formatter so section-level scripts never re-implement it */
  window.AkiTheme = window.AkiTheme || {};
  window.AkiTheme.formatMoney = formatMoney;

  /* Keep every cart badge in sync from one place */
  on('cart:updated', function (e) {
    var count = e.detail ? e.detail.item_count : 0;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
  });

  /* ------------------------------------------
     Focus trap helper for drawers
     ------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function handle(e) {
      if (e.key === 'Escape') { onEscape(); return; }
      if (e.key !== 'Tab') return;

      var items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', handle);
    return function release() { document.removeEventListener('keydown', handle); };
  }

  /* Only one element may own the scroll lock at a time */
  var lockOwners = new Set();
  function lockScroll(owner) {
    lockOwners.add(owner);
    document.body.classList.add('is-locked');
  }
  function unlockScroll(owner) {
    lockOwners.delete(owner);
    if (!lockOwners.size) document.body.classList.remove('is-locked');
  }

  /* ==========================================
     <aki-drawer>
     Generic slide-over: mobile nav and cart.
     ========================================== */
  class AkiDrawer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.panel = this.querySelector('[data-drawer-panel]');
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.releaseFocus = null;
      this.opener = null;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target === this.overlay) {
          e.preventDefault();
          this.close();
        }
      });

      /* Any button anywhere can open this drawer by id.
         The document listener outlives a theme-editor section reload, so it
         bails out once this element is no longer in the document. */
      document.addEventListener('click', (e) => {
        if (!document.contains(this)) return;
        var trigger = e.target.closest('[data-drawer-open="' + this.id + '"]');
        if (!trigger) return;
        e.preventDefault();
        this.open(trigger);
      });
    }

    open(opener) {
      if (this.hasAttribute('data-open')) return;
      this.opener = opener || null;
      this.setAttribute('data-open', 'true');
      if (this.panel) this.panel.setAttribute('data-open', 'true');
      if (this.overlay) this.overlay.setAttribute('data-open', 'true');
      lockScroll(this);
      this.releaseFocus = trapFocus(this.panel || this, () => this.close());

      window.requestAnimationFrame(() => {
        var target = this.panel && this.panel.querySelector('[data-drawer-close]');
        if (target) target.focus();
      });
      emit('drawer:open', { id: this.id });
    }

    close() {
      if (!this.hasAttribute('data-open')) return;
      this.removeAttribute('data-open');
      if (this.panel) this.panel.removeAttribute('data-open');
      if (this.overlay) this.overlay.removeAttribute('data-open');
      unlockScroll(this);
      if (this.releaseFocus) { this.releaseFocus(); this.releaseFocus = null; }
      if (this.opener && document.contains(this.opener)) this.opener.focus();
      emit('drawer:close', { id: this.id });
    }
  }

  /* ==========================================
     <aki-cart-drawer>
     Re-renders itself through the Section Rendering API
     so the markup always comes from Liquid — one source
     of truth for prices, discounts and translations.
     ========================================== */
  class AkiCartDrawer extends AkiDrawer {
    connectedCallback() {
      super.connectedCallback();
      if (this._cartReady) return;
      this._cartReady = true;

      this.sectionId = this.dataset.sectionId;

      this.addEventListener('click', (e) => {
        var remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.updateLine(remove.dataset.key, 0); return; }

        var minus = e.target.closest('[data-cart-minus]');
        if (minus) { this.updateLine(minus.dataset.key, Number(minus.dataset.qty) - 1); return; }

        var plus = e.target.closest('[data-cart-plus]');
        if (plus) { this.updateLine(plus.dataset.key, Number(plus.dataset.qty) + 1); return; }

        var upsell = e.target.closest('[data-cart-upsell-add]');
        if (upsell) { e.preventDefault(); this.addUpsell(upsell); }
      });

      on('cart:updated', () => this.render());
    }

    setBusy(busy) {
      var body = this.querySelector('[data-drawer-body]');
      if (body) body.style.opacity = busy ? '0.5' : '';
      this.querySelectorAll('button').forEach(function (b) {
        if (b.hasAttribute('data-drawer-close')) return;
        b.disabled = busy;
      });
    }

    async updateLine(key, quantity) {
      if (!key) return;
      this.setBusy(true);
      try {
        await Cart.change(key, Math.max(0, quantity));
      } catch (err) {
        console.error('aki: cart update failed', err);
        this.setBusy(false);
      }
    }

    async addUpsell(button) {
      var id = Number(button.dataset.variantId);
      if (!id) return;
      button.dataset.loading = 'true';
      try {
        await Cart.add([{ id: id, quantity: Number(button.dataset.quantity) || 1 }]);
      } catch (err) {
        console.error('aki: upsell failed', err);
      } finally {
        delete button.dataset.loading;
      }
    }

    /* Ask Liquid to re-render this section, then swap the inner markup */
    async render() {
      if (!this.sectionId) return;
      try {
        var url = window.Shopify.routes.root + '?section_id=' + encodeURIComponent(this.sectionId);
        var res = await fetch(url);
        if (!res.ok) return;
        var html = await res.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var nextBody = doc.querySelector('[data-drawer-body]');
        var nextFoot = doc.querySelector('[data-drawer-foot]');
        var currentBody = this.querySelector('[data-drawer-body]');
        var currentFoot = this.querySelector('[data-drawer-foot]');

        if (nextBody && currentBody) currentBody.innerHTML = nextBody.innerHTML;
        if (nextFoot && currentFoot) currentFoot.innerHTML = nextFoot.innerHTML;

        this.setBusy(false);
      } catch (err) {
        console.error('aki: cart drawer render failed', err);
        this.setBusy(false);
      }
    }
  }

  /* ==========================================
     <aki-bundle>
     Quantity-based offer selector.
     Writes the chosen quantity into the product form
     and mirrors price + label into the CTA.
     ========================================== */
  class AkiBundle extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.inputs = Array.from(this.querySelectorAll('[data-bundle-input]'));
      this.form = this.closest('form') || document.querySelector('[data-product-form]');
      this.qtyField = this.form && this.form.querySelector('[data-quantity-field]');

      this.inputs.forEach((input) => {
        input.addEventListener('change', () => this.sync());
      });

      this.sync();
    }

    get selected() {
      return this.inputs.find(function (i) { return i.checked; }) || this.inputs[0];
    }

    sync() {
      var input = this.selected;
      if (!input) return;

      var quantity = Number(input.dataset.quantity) || 1;
      var total = Number(input.dataset.total) || 0;

      if (this.qtyField) this.qtyField.value = quantity;

      /* Update every CTA that mirrors this selector */
      document.querySelectorAll('[data-bundle-mirror-price]').forEach(function (el) {
        el.textContent = formatMoney(total);
      });
      document.querySelectorAll('[data-bundle-mirror-qty]').forEach(function (el) {
        el.textContent = quantity;
      });

      emit('bundle:change', { quantity: quantity, total: total, name: input.dataset.name || '' });
    }
  }

  /* ==========================================
     <aki-product-form>
     AJAX add-to-cart with real loading and error states.
     ========================================== */
  class AkiProductForm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-to-cart]');
      this.errorEl = this.querySelector('[data-form-error]');
      if (!this.form) return;

      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    async onSubmit(e) {
      e.preventDefault();
      if (this.button && this.button.dataset.loading === 'true') return;

      this.clearError();
      if (this.button) this.button.dataset.loading = 'true';

      var data = new FormData(this.form);
      var id = Number(data.get('id'));
      var quantity = Number(data.get('quantity')) || 1;

      try {
        await Cart.add([{ id: id, quantity: quantity }]);

        /* Announce it. The upsell popup listens and may claim the moment;
           if nothing claims it, fall through to the cart drawer. */
        var claimed = false;
        var upsell = document.querySelector('aki-upsell-popup');
        if (upsell) {
          claimed = true;
          document.dispatchEvent(new CustomEvent('aki:added-to-cart', {
            detail: { id: id, quantity: quantity }
          }));
        }

        if (!claimed) {
          var drawer = document.getElementById('aki-cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            drawer.open(this.button);
          } else {
            window.location.href = window.Shopify.routes.root + 'cart';
          }
        }
      } catch (err) {
        console.error('aki: add to cart failed', err);
        this.showError(err.message || t('cartError', 'Das hat nicht geklappt. Bitte versuch es noch einmal.'));
      } finally {
        if (this.button) delete this.button.dataset.loading;
      }
    }
  }

  /* ==========================================
     <aki-gallery>
     Scroll-snap gallery. Thumbs, dots and counter
     all derive from scroll position — no state to desync.
     ========================================== */
  class AkiGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.counter = this.querySelector('[data-gallery-counter]');
      if (!this.track || this.slides.length < 1) return;

      this.index = 0;

      this.thumbs.concat(this.dots).forEach((btn) => {
        btn.addEventListener('click', () => this.goTo(Number(btn.dataset.index) || 0));
      });

      /* Derive the active slide from scroll, throttled to a frame */
      var ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          this.onScroll();
          ticking = false;
        });
      }, { passive: true });

      this.update();
    }

    onScroll() {
      var width = this.track.clientWidth || 1;
      var next = Math.round(this.track.scrollLeft / width);
      if (next !== this.index) { this.index = next; this.update(); }
    }

    goTo(index) {
      this.track.scrollTo({ left: this.track.clientWidth * index, behavior: 'smooth' });
      this.index = index;
      this.update();
    }

    update() {
      var total = this.slides.length;
      var current = Math.min(Math.max(this.index, 0), total - 1);

      this.thumbs.forEach(function (btn, i) {
        btn.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
      this.dots.forEach(function (btn, i) {
        btn.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
      if (this.counter) this.counter.textContent = (current + 1) + ' / ' + total;
    }
  }

  /* ==========================================
     <aki-accordion>
     Animated with grid-template-rows so it works
     without measuring heights.
     ========================================== */
  class AkiAccordion extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.single = this.hasAttribute('data-single');
      this.items = Array.from(this.querySelectorAll('[data-acc-item]'));

      this.items.forEach((item) => {
        var trigger = item.querySelector('[data-acc-trigger]');
        var panel = item.querySelector('[data-acc-panel]');
        if (!trigger || !panel) return;

        trigger.addEventListener('click', () => {
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (this.single) this.items.forEach((other) => { if (other !== item) this.setItem(other, false); });
          this.setItem(item, !isOpen);
        });
      });
    }

    setItem(item, open) {
      var trigger = item.querySelector('[data-acc-trigger]');
      var panel = item.querySelector('[data-acc-panel]');
      if (!trigger || !panel) return;
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.setAttribute('data-open', open ? 'true' : 'false');
    }
  }

  /* ==========================================
     <aki-sticky-atc>
     Shows only once the real buy box has scrolled away.
     ========================================== */
  class AkiStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      var targetSel = this.dataset.watch;
      var target = targetSel && document.querySelector(targetSel);
      if (!target || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.setAttribute('data-visible', entry.isIntersecting ? 'false' : 'true');
        });
      }, { rootMargin: '0px 0px -40px 0px', threshold: 0 });

      this.observer.observe(target);

      var btn = this.querySelector('[data-sticky-add]');
      if (btn) {
        btn.addEventListener('click', function () {
          var form = document.querySelector('[data-product-form] form') || document.querySelector('[data-product-form]');
          if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
      }

      on('bundle:change', (e) => {
        var priceEl = this.querySelector('[data-bundle-mirror-price]');
        if (priceEl) priceEl.textContent = formatMoney(e.detail.total);
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ==========================================
     <aki-announcement>
     Rotates messages. Pauses on hover and when the
     tab is hidden. Skips entirely for one message.
     ========================================== */
  class AkiAnnouncement extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.items = Array.from(this.querySelectorAll('[data-announce-item]'));
      if (this.items.length < 2) return;

      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) return;

      this.index = 0;
      this.interval = (Number(this.dataset.interval) || 5) * 1000;

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.stop() : this.start();
      });

      this.start();
    }

    start() {
      this.stop();
      this.timer = window.setInterval(() => this.next(), this.interval);
    }

    stop() {
      if (this.timer) { window.clearInterval(this.timer); this.timer = null; }
    }

    next() {
      this.items[this.index].setAttribute('data-active', 'false');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].setAttribute('data-active', 'true');
    }

    disconnectedCallback() { this.stop(); }
  }

  /* ==========================================
     Header state — sets --header-h and the scrolled flag
     ========================================== */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    function measure() {
      document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        header.setAttribute('data-scrolled', window.scrollY > 8 ? 'true' : 'false');
        ticking = false;
      });
    }

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    onScroll();
  }

  /* ==========================================
     Reveal on scroll — CSS does the animation
     ========================================== */
  function initReveal() {
    var nodes = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!nodes.length || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ==========================================
     Registration
     ========================================== */
  function define(name, ctor) {
    if (!window.customElements.get(name)) window.customElements.define(name, ctor);
  }

  define('aki-drawer', AkiDrawer);
  define('aki-cart-drawer', AkiCartDrawer);
  define('aki-bundle', AkiBundle);
  define('aki-product-form', AkiProductForm);
  define('aki-gallery', AkiGallery);
  define('aki-accordion', AkiAccordion);
  define('aki-sticky-atc', AkiStickyAtc);
  define('aki-announcement', AkiAnnouncement);

  function boot() {
    initHeader();
    initReveal();
    Cart.get().catch(function () { /* cart badge stays at its server-rendered value */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor: re-run non-custom-element setup after a section reloads */
  document.addEventListener('shopify:section:load', function () {
    initHeader();
    initReveal();
  });
  document.addEventListener('shopify:section:unload', initHeader);
})();
